import { Box, Chip, Link, TextField, Typography, Divider, IconButton, Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { BackgroundTypography } from "components/Text";
import { BaseSyntheticEvent, FC, useState, useMemo, useEffect } from "react";
import { ACTIONS, STEPS } from "../config";
import { SuccessRubric } from "modals/SuccessRubric";
import { FocusFeedbackChip } from "../Components/FocusFeedbackChip";
import { ErrorType, RequestFeedbackData } from "../types";
import { RequestFeedbackSteps } from "helpers/constants";
import { RateSelector } from "../Components/RateSelector";
import { Tag } from "types/tag";
import { REQUIREMENT } from "helpers/constants";
import { scrollToElementFromBottom } from "utils/scroll";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { getRequirementDisplayData } from "utils/feedbackIter";

type ReflectCoachProps = {
  onUpdate: (data: any) => void;
  data: RequestFeedbackData;
  handleStep: (step: string, action?: string) => void;
  error: ErrorType;
  tags: Tag[];
  customLabels: any[];
  historicalFeedbacks?: FeedbackType[];
  allRequirements?: Requirement[];
  /** When true, back returns to Perform (learner Reflect is skipped). */
  skipLearnerReflect?: boolean;
};

export const ReflectCoach: FC<ReflectCoachProps> = ({
  onUpdate,
  data,
  handleStep,
  error,
  tags,
  customLabels,
  historicalFeedbacks = [],
  allRequirements = [],
  skipLearnerReflect = false,
}) => {
  const [openSuccessRubric, setOpenSuccessRubric] = useState(false);
  const [openSubRequirementsModal, setOpenSubRequirementsModal] = useState(false);

  const isITER = useMemo(() => {
    return data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;
  }, [data.selectedCompetencySkill]);

  // Build ratings by requirement for ITER competencies
  const buildRatingsByRequirementForIter = useMemo(() => {
    if (!isITER || !data.selectedCompetencySkill?.name) {
      return {};
    }

    const ratingsByRequirement: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    > = {};

    const iterName = data.selectedCompetencySkill.name;
    const history = historicalFeedbacks
      .filter((fb) => {
        const requirementData = getRequirementDisplayData(fb, allRequirements);
        return requirementData.isITER && requirementData.name === iterName;
      })
      .sort((a, b) => {
        const da = a.observation_date || a.created_at || "";
        const db = b.observation_date || b.created_at || "";
        return da.localeCompare(db);
      });

    history.forEach((fb) => {
      const date = (fb.observation_date || fb.created_at) as string;
      fb.feedback_skills.forEach((skill) => {
        const reqId = skill.requirement?.requirement_id;
        if (!reqId) return;
        if (!ratingsByRequirement[reqId]) {
          ratingsByRequirement[reqId] = [];
        }
        ratingsByRequirement[reqId].push({
          date,
          learnerRating: skill.self_rating ?? null,
          coachRating: skill.coach_rating ?? null,
        });
      });
    });

    return ratingsByRequirement;
  }, [isITER, data.selectedCompetencySkill, historicalFeedbacks, allRequirements]);

  const subRequirements = useMemo(() => {
    if (!isITER || !data.selectedCompetencySkill?.competencies) {
      return [];
    }
    return data.selectedCompetencySkill.competencies
      .map((c: any) => c.competency)
      .filter(Boolean);
  }, [isITER, data.selectedCompetencySkill]);

  // Scroll to first missing ITER coach rating when errors appear
  useEffect(() => {
    if (isITER && error.iterSubRequirementCoachRateErrors) {
      const firstMissingRatingId = Object.keys(error.iterSubRequirementCoachRateErrors).find(
        (requirementId) => error.iterSubRequirementCoachRateErrors![requirementId] !== null
      );
      if (firstMissingRatingId) {
        scrollToElementFromBottom(`iter-coach-rating-${firstMissingRatingId}`);
      }
    } else if (error.coachRate && !isITER) {
      // For non-ITER, scroll to the rate selector
      scrollToElementFromBottom("coach-rate-selector");
    }
  }, [error.iterSubRequirementCoachRateErrors, error.coachRate, isITER]);

  return (
    <StepLayoutContainer
      title={
        skipLearnerReflect
          ? RequestFeedbackSteps.titleGiveFeedback
          : "Ask, Listen and plan"
      }
      onClickNext={() => handleStep(STEPS.SUBMIT)}
      onClickBack={() =>
        handleStep(skipLearnerReflect ? STEPS.PERFORM : STEPS.REFLECT, "back")
      }
      marginXSBottom={8.5}
    >
      <Box my={2}>
        <Typography>
          {skipLearnerReflect
            ? RequestFeedbackSteps.reminderGiveFeedback
            : "Need a reminder? Review the goals and success rubric of the skill you selected to perform."}{" "}
          <Link
            sx={{ cursor: "pointer" }}
            onClick={() => {
              setOpenSuccessRubric(true);
            }}
          >
            See Requirement summary
          </Link>
        </Typography>

        <BackgroundTypography
          text={
            skipLearnerReflect
              ? RequestFeedbackSteps.sectionGiveFeedback
              : "Coach Feedback"
          }
        />

        <Typography variant="body1">
          1.{" "}
          {skipLearnerReflect
            ? RequestFeedbackSteps.questionFocusFeedbackGive
            : RequestFeedbackSteps.questionFocusFeedback}{" "}
        </Typography>
        <Box mt={2}>
          <Box>
            {tags.map((tag) => (
              <FocusFeedbackChip
                key={tag.tag_id}
                focus={tag}
                selectedFocusFeedback={data.selectedFocusFeedback}
                onUpdate={(payload) =>
                  onUpdate({
                    type: ACTIONS.SET_SELECTED_FOCUS_FEEDBACK,
                    payload,
                  })
                }
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box>
        <Typography variant="body1">
          2.{" "}
          {skipLearnerReflect
            ? RequestFeedbackSteps.question1ReflectCoachDetail
            : RequestFeedbackSteps.question1ReflectCoach}
          <Typography component={"span"} color="error">
            *
          </Typography>
        </Typography>
        <Typography>
          {skipLearnerReflect
            ? RequestFeedbackSteps.guideGiveFeedback
            : "Here are some suggestions to guide your Coach's feedback:"}
        </Typography>
        <ul style={{ listStyleType: "disc" }}>
          <li>
            {skipLearnerReflect
              ? RequestFeedbackSteps.guideObserveGive
              : "What did your coach observe?"}
          </li>
          <li>
            {skipLearnerReflect
              ? RequestFeedbackSteps.guideChangeGive
              : "What can you change next time?"}
          </li>
          <li>
            {skipLearnerReflect
              ? RequestFeedbackSteps.guidePlanGive
              : "What is the plan for next time to implement/practice the change?"}
          </li>
          {data.selectedFocusFeedback.length > 0 && (
            <li>
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                {skipLearnerReflect
                  ? RequestFeedbackSteps.guideFocusGive
                  : "What specific feedback do they have on"}{" "}
                {data.selectedFocusFeedback.map((f: Tag) => (
                  <Chip
                    key={f.tag_id}
                    label={f.name}
                    color="success"
                    variant="filled"
                    size="small"
                    sx={{ mx: 0.25 }}
                  />
                ))}
                ?
              </Box>
            </li>
          )}
        </ul>
        <Box my={2}>
          <TextField
            multiline
            rows={4}
            fullWidth
            onChange={(e: BaseSyntheticEvent) => {
              onUpdate({
                type: ACTIONS.SET_COACH_OBSERVE,
                payload: e.target.value,
              });
            }}
            value={data.coachObserve}
            error={error.coachObserve !== null}
            helperText={error.coachObserve}
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="body1">
          3.{" "}
          {skipLearnerReflect
            ? RequestFeedbackSteps.question2ReflectCoachDetail
            : RequestFeedbackSteps.question2ReflectCoach}
          <Typography component={"span"} color="error">
            *
          </Typography>
        </Typography>
        {isITER && subRequirements.length > 0 ? (
          <Box mt={2}>
            {subRequirements.map((subReq: any, index: number) => {
              const ratingError = error.iterSubRequirementCoachRateErrors?.[subReq.requirement_id];
              return (
                <Box
                  key={subReq.requirement_id}
                  id={`iter-coach-rating-${subReq.requirement_id}`}
                  sx={{ mb: 3 }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body1" fontWeight={600}>
                      {subReq.name}
                    </Typography>
                    <Tooltip title="View Competencies">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenSubRequirementsModal(true);
                        }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {subReq.description && (
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                      {subReq.description}
                    </Typography>
                  )}
                  <RateSelector
                    value={data.iterSubRequirementCoachRates[subReq.requirement_id] || null}
                    onChange={(value: number) => {
                      onUpdate({
                        type: ACTIONS.SET_ITER_SUB_REQUIREMENT_COACH_RATE,
                        payload: {
                          requirementId: subReq.requirement_id,
                          rate: value,
                        },
                      });
                    }}
                    requirement={subReq}
                    customLabels={customLabels}
                  />
                  {ratingError && (
                    <Typography component="div" color="error" sx={{ mt: 1, fontSize: "0.875rem" }}>
                      {ratingError}
                    </Typography>
                  )}
                  {index < subRequirements.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box mt={2} id="coach-rate-selector">
            <RateSelector
              value={data.coachRate}
              onChange={(value: number) => {
                onUpdate({ type: ACTIONS.SET_COACH_RATE, payload: value });
              }}
              requirement={data.selectedCompetencySkill}
              customLabels={customLabels}
            />
            {!!error.coachRate && (
              <Typography component="div" color="error" sx={{ mt: 1 }}>
                {error.coachRate}
              </Typography>
            )}
          </Box>
        )}

        {openSuccessRubric && data.selectedCompetencySkill && (
          <SuccessRubric
            open={openSuccessRubric}
            onClose={() => setOpenSuccessRubric(false)}
            requirement={data.selectedCompetencySkill}
            customLabels={customLabels}
          />
        )}
      </Box>

      {isITER && data.selectedCompetencySkill && (
        <SubRequirementsModal
          open={openSubRequirementsModal}
          onClose={() => setOpenSubRequirementsModal(false)}
          iterName={data.selectedCompetencySkill.name}
          subRequirements={subRequirements.map((subReq: any) => ({
            requirement_id: subReq.requirement_id,
            name: subReq.name,
            description: subReq.description || null,
          }))}
          ratingsByRequirement={buildRatingsByRequirementForIter}
        />
      )}
    </StepLayoutContainer>
  );
};
